import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import { withAppHOC } from '../../hoc';
import { StyledContentContainer } from '../../components/styled';
import PageHeader from '../../components/PageHeader/PageHeader';
import { MessageBox } from '../../components/FormElements';
import Loader from '../../components/Loader/Loader';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import NoData from '../../components/NoData/NoData';
import { TableBuilder } from '../../components/TableElements';
import Pagination from '../../components/Pagination/Pagination';
import API from '../../api';
import { RESPONSE_STATUSES } from '../../constants';
import { getSearchParams } from '../../utils';

const Downloads = () => {
  const [paginationData, setPaginationData] = useState({
    pageNumber: getSearchParams('pageNumber') || 1,
    recordsPerPage: getSearchParams('recordsPerPage') || 30,
  });

  const readData = async () => {
    try {
      const response = await API.get(
        `/reports/downloads?pageNumber=${paginationData.pageNumber}&recordsPerPage=${paginationData.recordsPerPage}`
      );
      return response.data;
    } catch (error) {
      throw new Error(error);
    }
  };

  const { data, isLoading, error, refetch, isRefetching } = useQuery({
    queryKey: ['downloads', paginationData],
    queryFn: readData,
  });

  const reloadData = async () => await refetch();

  const renderPageHeading = () => (
    <PageHeader
      title="Downloads"
      showAddButton={false}
      showReloadData={true}
      reloadData={reloadData}
      showExportButton={false}
    />
  );

  if (isLoading) {
    return (
      <>
        {renderPageHeading()}
        <StyledContentContainer>
          <LoadingSpinner />
        </StyledContentContainer>
      </>
    );
  }

  if (error) {
    return (
      <>
        {renderPageHeading()}
        <StyledContentContainer>
          <MessageBox
            status={RESPONSE_STATUSES.FAILED}
            message={error.message}
          />
        </StyledContentContainer>
      </>
    );
  }

  return (
    <>
      <Loader isLoading={isRefetching} />
      {renderPageHeading()}
      <StyledContentContainer graybackground="true">
        {data?.status === RESPONSE_STATUSES.FAILED && (
          <MessageBox
            status={RESPONSE_STATUSES.FAILED}
            message={data?.message}
          />
        )}
        {data?.data?.length === 0 ? (
          <NoData />
        ) : (
          <>
            <TableBuilder
              hasFilters={false}
              isLoading={isLoading}
              tableHeadings={[
                {
                  title: 'Requested On',
                  dataSelector: 'createdOn',
                  dataType: 'string',
                },
                {
                  title: 'Data Type',
                  dataSelector: 'dataType',
                  dataType: 'string',
                },
                {
                  title: 'From Date',
                  dataSelector: 'fromDate',
                  dataType: 'string',
                },
                {
                  title: 'To Date',
                  dataSelector: 'toDate',
                  dataType: 'string',
                },
                {
                  title: 'Status',
                  dataSelector: 'status',
                  dataType: 'string',
                  align: 'center',
                  cellrenderer: (value, item) =>
                    value === 'Success' && item.url ? (
                      <FontAwesomeIcon
                        icon={faDownload}
                        color="#0095ff"
                        onClick={() => window.open(item.url, '_blank')}
                      />
                    ) : (
                      value
                    ),
                },
              ]}
              tableData={data?.data}
            />
            <Pagination
              totalRecords={data?.totalRecords}
              paginationData={paginationData}
              setPaginationData={setPaginationData}
            />
          </>
        )}
      </StyledContentContainer>
    </>
  );
};

export default withAppHOC(Downloads);
